/**
 * Parameter selection and configuration for edge detection algorithms
 * These parameters control the ray walking thresholds for green detection
 */

export interface LabSummary {
  med: Float32Array // [L, a, b] median
  p16: Float32Array // 16th percentile
  p84: Float32Array // 84th percentile
  spread: Float32Array // p84 - p16
  n: number
}

export interface GreenAutoMapParams {
  lPresetIndex: number
  abPresetIndex: number
  rayCount: number
  loosenessBias: number
  correctRayOutliers: boolean
  debugStopLogging: boolean
  debugStepLogging: boolean
  debugImageLogging: boolean
  abGate: number
  patchHalfPx: number
  stepPx: number
  probeRadiusM: number
  maxSearchRadiusM: number
}

export const GREEN_AUTO_MAP_MIN_PIX = 80
export const GREEN_AUTO_MAP_PROBE_MIN_PIX = 15

export const DEFAULT_GREEN_AUTO_MAP_PARAMS: GreenAutoMapParams = {
  lPresetIndex: -1,
  abPresetIndex: -1,
  rayCount: 16,
  loosenessBias: 0,
  correctRayOutliers: true,
  debugStopLogging: false,
  debugStepLogging: false,
  debugImageLogging: false,
  abGate: 5.0,
  patchHalfPx: 13,
  stepPx: 7,
  probeRadiusM: 1.0,
  maxSearchRadiusM: 28,
}

export const GREEN_AUTO_MAP_SAMPLING_RADIUS_M = DEFAULT_GREEN_AUTO_MAP_PARAMS.maxSearchRadiusM

export const RAY_WALK_DISTANCE_LIMITS_M = {
  minStart: 0.5,
  minStep: 0.4,
  maxStart: 1.0,
} as const

/**
 * Edge detection parameter levels
 */
export type ParamLevel = 'strictest' | 'tight' | 'medium' | 'loose' | 'ultimate'

/**
 * Base parameters shared across all configurations
 */
const EDGE_PARAMS_BASE = {
  dStartFrac: 0.08,
  stepFrac: 0.02,
  dMaxFrac: 0.95,
  abGate: 5.0,
  ratioLGate: 0.95,
  Lw: 0.35,
}

export interface LParamSet {
  deMedStop: number
  lAbsStop: number
  lStepStop: number
}

export interface AbParamSet {
  minRatioAb: number
  maxMedShiftAb: number
  abStepStop: number
  abBaseStop: number
  abStepSumStop: number
  abBaseSumStop: number
}

export interface ProbeThresholdSet {
  maxAbsAb: number
  stdAb: number
  stdL: number
}

export type ProbeThresholdLevel = Exclude<ParamLevel, 'ultimate'>

export interface GreenAutoMapTuning {
  lParams: Record<ParamLevel, LParamSet>
  abParams: Record<ParamLevel, AbParamSet>
  probeThresholds: Record<ProbeThresholdLevel, ProbeThresholdSet>
}

const cloneTuning = (tuning: GreenAutoMapTuning): GreenAutoMapTuning => ({
  lParams: {
    strictest: { ...tuning.lParams.strictest },
    tight: { ...tuning.lParams.tight },
    medium: { ...tuning.lParams.medium },
    loose: { ...tuning.lParams.loose },
    ultimate: { ...tuning.lParams.ultimate },
  },
  abParams: {
    strictest: { ...tuning.abParams.strictest },
    tight: { ...tuning.abParams.tight },
    medium: { ...tuning.abParams.medium },
    loose: { ...tuning.abParams.loose },
    ultimate: { ...tuning.abParams.ultimate },
  },
  probeThresholds: {
    strictest: { ...tuning.probeThresholds.strictest },
    tight: { ...tuning.probeThresholds.tight },
    medium: { ...tuning.probeThresholds.medium },
    loose: { ...tuning.probeThresholds.loose },
  },
})

export const DEFAULT_GREEN_AUTO_MAP_TUNING: GreenAutoMapTuning = {
  lParams: {
    strictest: { deMedStop: 5.5, lAbsStop: 9.5, lStepStop: 3.3 },
    tight: { deMedStop: 7.5, lAbsStop: 12.5, lStepStop: 5 },
    medium: { deMedStop: 8, lAbsStop: 16, lStepStop: 6 },
    loose: { deMedStop: 9, lAbsStop: 17.5, lStepStop: 7.5 },
    ultimate: { deMedStop: 11, lAbsStop: 21, lStepStop: 10 },
  },
  abParams: {
    strictest: {
      minRatioAb: 0.5,
      maxMedShiftAb: 5,
      abStepStop: 2.5,
      abBaseStop: 4,
      abStepSumStop: 3.5,
      abBaseSumStop: 6,
    },
    tight: { minRatioAb: 0.4, maxMedShiftAb: 6, abStepStop: 3, abBaseStop: 5, abStepSumStop: 3.5, abBaseSumStop: 6.8 },
    medium: { minRatioAb: 0.2, maxMedShiftAb: 7, abStepStop: 4, abBaseStop: 5, abStepSumStop: 4.5, abBaseSumStop: 7 },
    loose: { minRatioAb: 0.1, maxMedShiftAb: 8, abStepStop: 7, abBaseStop: 7, abStepSumStop: 5.5, abBaseSumStop: 9 },
    ultimate: {
      minRatioAb: 0,
      maxMedShiftAb: 9.5,
      abStepStop: 10,
      abBaseStop: 9.5,
      abStepSumStop: 7.5,
      abBaseSumStop: 12,
    },
  },
  probeThresholds: {
    strictest: { maxAbsAb: 0.9, stdAb: 0.7, stdL: 0.65 },
    tight: { maxAbsAb: 3.1, stdAb: 1.6, stdL: 1.25 },
    medium: { maxAbsAb: 2.6, stdAb: 2.6, stdL: 2.2 },
    loose: { maxAbsAb: 3.4, stdAb: 3.4, stdL: 3 },
  },
}

let activeTuning: GreenAutoMapTuning = cloneTuning(DEFAULT_GREEN_AUTO_MAP_TUNING)

export const getActiveGreenAutoMapTuning = (): GreenAutoMapTuning => cloneTuning(activeTuning)

export const setActiveGreenAutoMapTuning = (tuning: GreenAutoMapTuning): void => {
  activeTuning = cloneTuning(tuning)
}

export const resetActiveGreenAutoMapTuning = (): void => {
  activeTuning = cloneTuning(DEFAULT_GREEN_AUTO_MAP_TUNING)
}

export const getInitialEdgeParams = (): EdgeParams => ({
  ...EDGE_PARAMS_BASE,
  ...activeTuning.lParams.medium,
  ...activeTuning.abParams.medium,
})

/**
 * Complete edge detection parameters
 */
export interface EdgeParams {
  // Base params
  dStartFrac: number
  stepFrac: number
  dMaxFrac: number
  abGate: number
  ratioLGate: number
  Lw: number

  // L params
  deMedStop: number
  lAbsStop: number
  lStepStop: number

  // AB params
  minRatioAb: number
  maxMedShiftAb: number
  abStepStop: number
  abBaseStop: number
  abStepSumStop: number
  abBaseSumStop: number
}

/**
 * Probe result containing green color analysis
 */
export interface ProbeResult {
  probeRadiusM: number
  offsetM: number
  summaries: Record<string, LabSummary | null>
  centerMed: Float32Array
  outerScoresToCenter: Record<string, number>
  outerDeCutoff: number
  keptKeys: string[]
  droppedMinpix: Record<string, number>
  droppedByDe: Record<string, [number, number]>
  baselineLab: Float32Array
  stdMedsKept: Float32Array
  maxAbsAbKept: number
  mpp: number
}

/**
 * Parameter selection debug information
 */
export interface ParamSelectionDebug {
  maxAbsAB: number
  stdAB: number
  stdL: number
  lLevel: ParamLevel
  abLevel: ParamLevel
  thresholds: {
    L: Record<ParamLevel, number>
    AB: Record<ParamLevel, { maxAbsAB: number; stdAB: number }>
  }
}

/**
 * Selects appropriate edge parameters based on probe analysis
 *
 * @param probe - Probe result containing green color statistics
 * @returns Tuple of [chosen name, parameters, debug info]
 */
export function selectEdgeParamsFromProbe(probe: ProbeResult): [string, EdgeParams, ParamSelectionDebug] {
  const [stdL, stdA, stdB] = probe.stdMedsKept
  const stdAB = stdA + stdB
  const maxAbsAB = probe.maxAbsAbKept

  const probeThresholds = activeTuning.probeThresholds

  let lLevel: ParamLevel
  if (stdL <= probeThresholds.strictest.stdL) {
    lLevel = 'strictest'
  } else if (stdL <= probeThresholds.tight.stdL) {
    lLevel = 'tight'
  } else if (stdL <= probeThresholds.medium.stdL) {
    lLevel = 'medium'
  } else if (stdL <= probeThresholds.loose.stdL) {
    lLevel = 'loose'
  } else {
    lLevel = 'ultimate'
  }

  let abLevel: ParamLevel
  if (maxAbsAB <= probeThresholds.strictest.maxAbsAb && stdAB <= probeThresholds.strictest.stdAb) {
    abLevel = 'strictest'
  } else if (maxAbsAB <= probeThresholds.tight.maxAbsAb && stdAB <= probeThresholds.tight.stdAb) {
    abLevel = 'tight'
  } else if (maxAbsAB <= probeThresholds.medium.maxAbsAb && stdAB <= probeThresholds.medium.stdAb) {
    abLevel = 'medium'
  } else if (maxAbsAB <= probeThresholds.loose.maxAbsAb && stdAB <= probeThresholds.loose.stdAb) {
    abLevel = 'loose'
  } else {
    abLevel = 'ultimate'
  }

  const params = buildEdgeParams(lLevel, abLevel)
  const chosenName = `L:${lLevel} | AB:${abLevel}`

  const dbg: ParamSelectionDebug = {
    maxAbsAB,
    stdAB,
    stdL,
    lLevel,
    abLevel,
    thresholds: {
      L: {
        strictest: probeThresholds.strictest.stdL,
        tight: probeThresholds.tight.stdL,
        medium: probeThresholds.medium.stdL,
        loose: probeThresholds.loose.stdL,
        ultimate: Infinity,
      },
      AB: {
        strictest: { maxAbsAB: probeThresholds.strictest.maxAbsAb, stdAB: probeThresholds.strictest.stdAb },
        tight: { maxAbsAB: probeThresholds.tight.maxAbsAb, stdAB: probeThresholds.tight.stdAb },
        medium: { maxAbsAB: probeThresholds.medium.maxAbsAb, stdAB: probeThresholds.medium.stdAb },
        loose: { maxAbsAB: probeThresholds.loose.maxAbsAb, stdAB: probeThresholds.loose.stdAb },
        ultimate: { maxAbsAB: Infinity, stdAB: Infinity },
      },
    },
  }

  return [chosenName, params, dbg]
}

/**
 * Builds the concrete parameter dict by merging base, L, and AB params
 *
 * @param lLevel - L parameter level
 * @param abLevel - AB parameter level
 * @returns Complete edge detection parameters
 */
export function buildEdgeParams(lLevel: ParamLevel, abLevel: ParamLevel): EdgeParams {
  return {
    ...EDGE_PARAMS_BASE,
    ...activeTuning.lParams[lLevel],
    ...activeTuning.abParams[abLevel],
  }
}

/**
 * Discrete levels array for moving between levels
 */
export const LEVELS: ParamLevel[] = ['strictest', 'tight', 'medium', 'loose', 'ultimate']

/**
 * Moves one step along parameter levels
 *
 * @param level - Current level
 * @param direction - Direction to move ('looser' or 'stricter')
 * @returns New level, or null if at boundary
 */
export function moveLevel(level: ParamLevel | null, direction: 'looser' | 'stricter'): ParamLevel | null {
  if (level === null) {
    return null
  }

  const idx = LEVELS.indexOf(level)
  if (idx === -1) {
    return null
  }

  if (direction === 'looser') {
    return idx === LEVELS.length - 1 ? null : LEVELS[idx + 1]
  } else if (direction === 'stricter') {
    return idx === 0 ? null : LEVELS[idx - 1]
  }

  return null
}
